import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import {
  INSURERS_LIST,
  NO_INSURER,
} from 'src/app/_constants/health-insurers-list';
import { formatDateForInput } from 'src/app/_helpers/utils';
import { FamilyMember } from 'src/app/_types/auth.types';
import { HealthInsuranceMetadata } from 'src/app/_types/lead.types';
import {
  CivilStatus,
  DentalInterval,
  getCivilStatusLabel,
  getDentalIntervalLabel,
  getHealthBaseInsuranceLabel,
  getPaymentMethodLabel,
  HealthBaseInsurance,
  PaymentMethod,
} from 'src/app/enums/health.enums';
import { InsuranceType } from 'src/app/enums/insurance-type.enum';
import {
  getPaymentFrequencyLabel,
  PaymentFrequency,
} from 'src/app/enums/payment-frequency.enum';

import { LeadFormBase } from '../lead-form-base';

@Component({
  selector: 'app-lead-health-form',
  templateUrl: './health-form.component.html',
  styleUrls: ['./health-form.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
})
export class HealthFormComponent extends LeadFormBase implements OnDestroy {
  @Input()
  formGroup!: FormGroup;

  @Input()
  familyMembers!: FamilyMember[];

  private _startDate!: string;

  get startDate(): string {
    return this._startDate;
  }

  set startDate(value: string) {
    this._startDate = value;
    if (value)
      this.metadataFormGroup.controls['startDate'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  private _startDatePrestation!: string;

  get startDatePrestation(): string {
    return this._startDatePrestation;
  }

  set startDatePrestation(value: string) {
    this._startDatePrestation = value;
    if (value)
      this.metadataFormGroup.controls['startDatePrestations'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  private _birthDate!: string;

  get birthDate(): string {
    return this._birthDate;
  }

  set birthDate(value: string) {
    this._birthDate = value;
    if (value)
      this.metadataFormGroup.controls['birthDate'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  private _currentInsuranceEndDate!: Date;

  get currentInsuranceEndDate(): Date {
    return this._currentInsuranceEndDate;
  }

  set currentInsuranceEndDate(value: Date) {
    this._currentInsuranceEndDate = value;
    if (value)
      (
        this.metadataFormGroup.controls['currentInsurance'] as FormGroup
      ).controls['endDate'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  withBaseInsurance: boolean = true;

  withSupplementaryInsurance: boolean = true;

  memberIdSubscription!: Subscription;

  constructor(private fb: FormBuilder) {
    super();
  }

  metadataFormGroup: FormGroup = this.fb.group({
    type: this.fb.control(InsuranceType.HEALTH),
    postalCode: this.fb.control(null),
    birthDate: this.fb.control(null),
    sex: this.fb.control(null),
    franchise: this.fb.control(null),
    baseInsurance: this.fb.control(null),
    hospitalInsurance: this.fb.control(null),
    startDate: this.fb.control(null),
    startDatePrestations: this.fb.control(null),

    city: this.fb.control(null),
    paymentMethod: this.fb.control(null),
    paymentFrequency: this.fb.control(null),
    iban: this.fb.control(null),
    bankNameAddress: this.fb.control(null),
    avsNumber: this.fb.control(null),
    job: this.fb.control(null),
    healthInfo: this.fb.group({
      hospitalStay: this.fb.control(null),
      hasTreatment: this.fb.control(null),
      hasTransplant: this.fb.control(null),
      hasMedication: this.fb.control(null),
      hasDiagnostic: this.fb.control(null),
      insuranceRefused: this.fb.control(null),
      jawProblem: this.fb.control(null),
      smokeOrDrink: this.fb.control(null),
      birthProblem: this.fb.control(null),
      hasHeartOrPsychologicalOrTumor: this.fb.control(null),
      hasEmergencyIntervention: this.fb.control(null),
      hasSurgery: this.fb.control(null),
      isSmoker: this.fb.control(null),
      isDrinker: this.fb.control(null),
      hasDentalTreatment: this.fb.control(null),
      dentalTreatmentDetails: this.fb.control(null),
      dentalCheckInterval: this.fb.control(null),
      dentalCleaningInterval: this.fb.control(null),
    }),
    doctor: this.fb.group({
      firstname: this.fb.control(null),
      lastname: this.fb.control(null),
      street: this.fb.control(null),
      streetNumber: this.fb.control(null),
      zipCode: this.fb.control(null),
      city: this.fb.control(null),
    }),
    alreadyGMClient: this.fb.control(null),
    alreadySwicaClient: this.fb.control(null),
    alreadyCssClient: this.fb.control(null),
    alreadyHelsanaClient: this.fb.control(null),
    helsanaNumber: this.fb.control(null),
    refusedOrResctricted: this.fb.control(null),
    personalInfo: this.fb.group({
      height: this.fb.control(null),
      weight: this.fb.control(null),
      bmi: this.fb.control(null),
    }),
    currentInsurance: this.fb.group({
      name: this.fb.control(null),
      associateNumber: this.fb.control(null),
      endDate: this.fb.control(null),
    }),
  });

  setMemberInfo(familyMemberId: string) {
    if (!this.familyMembers || this.familyMembers.length === 0) return;
    const familyMember = this.familyMembers.find(
      (x) => x.familyMemberId === familyMemberId
    );
    this.metadataFormGroup.controls['birthDate'].setValue(
      new Date(familyMember?.dob!).toLocaleDateString('fr-FR')
    );
    this.metadataFormGroup.controls['sex'].setValue(familyMember?.gender);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const healthMetadata = this.metadata as HealthInsuranceMetadata;
      this.metadataFormGroup.patchValue(this.metadata);
      this.startDate = formatDateForInput(healthMetadata.startDate);
      this.startDatePrestation = formatDateForInput(
        healthMetadata.startDatePrestations!
      );
      this.birthDate = formatDateForInput(healthMetadata.birthDate);
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    if (this.formGroup.controls['memberId'].value) {
      this.setMemberInfo(this.formGroup.controls['memberId'].value);
    }

    this.memberIdSubscription = this.formGroup.controls[
      'memberId'
    ].valueChanges.subscribe((value) => {
      this.setMemberInfo(value);
    });
  }

  baseInsuranceValues = Object.values(HealthBaseInsurance);

  getBaseInsuranceLabel = getHealthBaseInsuranceLabel;

  paymentMethodValues = Object.values(PaymentMethod);

  getPaymentMethodLabel = getPaymentMethodLabel;

  paymentFrequencyValues = Object.values(PaymentFrequency);

  getPaymentFrequencyLabel = getPaymentFrequencyLabel;

  civilStatusValues = Object.values(CivilStatus);

  getCivilStatusLabel = getCivilStatusLabel;

  insurersList = INSURERS_LIST;

  noInsurer = NO_INSURER;

  hasInsurer() {
    return (
      !this.metadataFormGroup ||
      !this.metadataFormGroup.get('currentInsurance') ||
      (this.metadataFormGroup.get('currentInsurance') as FormGroup).get('name')
        ?.value !== this.noInsurer
    );
  }

  dentalIntervalValues = Object.values(DentalInterval);

  getDentalIntervalLabel = getDentalIntervalLabel;

  ngOnDestroy(): void {
    this.memberIdSubscription.unsubscribe();
  }

  override handleSubmit(): Observable<boolean> {
    Object.keys(this.metadataFormGroup.controls).forEach((x) => {
      if (
        this.metadataFormGroup.controls[x].value &&
        typeof this.metadataFormGroup.controls[x].value === 'object' &&
        (Object.keys(this.metadataFormGroup.controls[x].value).length === 0 ||
          Object.keys(this.metadataFormGroup.controls[x].value).every(
            (key) => !this.metadataFormGroup.controls[x].value[key]
          ))
      ) {
        this.metadataFormGroup.removeControl(x);
      }
    });
    return of(true);
  }

  getControl(name: string): FormControl {
    return this.metadataFormGroup.controls[name] as FormControl;
  }
}
